import { showErrorSnackbar, type ErrorSnackbarOptions } from "_shared/_errors/error-snackbar";
import type { SnackbarKey } from "_shared/_providers/snackbars/snackbars";

/** What a proposal command refused because the proposal moved on says. */
export const PROPOSAL_CHANGED_MESSAGE =
  "This proposal changed after you opened it. Review the latest proposal before you send this action again.";

/**
 * Shows why a proposal command failed: the field reasons the API gives, or, for anything the person
 * cannot fix, that support was notified (#6614).
 *
 * Until #6614 every proposal page showed the API's `detail` whenever it had one. Under the generic
 * `bad_request` code that `detail` is an exception's text, so readers saw "CollectionItemUpdated
 * requires an existing item and value." (#6612) and "Proposal validation failed for: vendorTeam."
 * (#6613), and a fallback such as "Support staff have been notified" that notified nobody.
 *
 * A lifecycle command rejects with the RTK result itself (`{ error: { status, data } }`) where a save
 * rejects with the unwrapped error, so both are read.
 */
export function showProposalCommandError(
  failure: unknown,
  doing: string,
  operation: string,
  options: ErrorSnackbarOptions = {},
): SnackbarKey {
  return showErrorSnackbar(
    rejectedResponse(failure),
    { doing, operation, staleMessage: PROPOSAL_CHANGED_MESSAGE },
    options,
  );
}

function rejectedResponse(failure: unknown): unknown {
  if (!isRecord(failure) || "status" in failure || !isRecord(failure.error)) return failure;
  return "status" in failure.error ? failure.error : failure;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
